import React, { useState, useEffect } from 'react';
import { FaTachometerAlt, FaTrash, FaProjectDiagram, FaUsers, FaSignOutAlt, FaUserCircle, FaUserPlus, FaChevronDown, FaTruck, FaBars, FaTimes } from 'react-icons/fa';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './Login';
import CreateUser from './CreateUser';
import WasteTickets from './WasteTickets';
import Projects from './Projects';
import WasteCarriers from './waste_carriers';
import WasteDropoffSites from './waste_dropoff_sites';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'leaflet/dist/leaflet.css';
import WasteTicketMap from './components/WasteTicketMap'; 
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, LineChart, Line } from 'recharts';
import { setupAxiosInterceptors } from './axiosSetup';
import API_CONFIG from './config';
import Users from './Users';
import './styles/App.css';
dayjs.extend(customParseFormat);

const customInputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    fontSize: '1em',
    borderRadius: '8px',
    border: '1px solid #d0d0d0',
    marginTop: 2,
    boxSizing: 'border-box',
    background: '#fff',
    minHeight: '40px',
    height: '40px'
};

const inputWrapperStyle: React.CSSProperties = {
    position: 'relative',
    width: '100%'
};

const iconStyle: React.CSSProperties = {
    position: 'absolute',
    right: 16,
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#888',
    pointerEvents: 'none'
};

const pickerGlobalStyles = (
    <style>
        {`
        .custom-picker,
        .react-datepicker-wrapper,
        .react-time-picker {
            width: 100%;
        }
        .react-datepicker__input-container input.custom-picker {
            width: 100%;
            padding: 12px;
            font-size: 1em;
            border-radius: 8px;
            border: 1px solid #d0d0d0;
            margin-top: 2px;
            box-sizing: border-box;
            background: #fff;
        }
        .react-time-picker__wrapper {
            border-radius: 8px;
            border: 1px solid #d0d0d0;
            padding: 0 8px;
            background: #fff;
        }
        .react-time-picker__inputGroup {
            padding: 12px 0;
            font-size: 1em;
        }
        `}
    </style>
);

const logoutItemStyle: React.CSSProperties = {
    margin: '32px 0',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '32px',
    fontSize: '1.25em',
    color: '#d32f2f',
    cursor: 'pointer',
    fontFamily: 'Segoe UI, Arial, sans-serif'
};

const navItemStyle: React.CSSProperties = {
    margin: '32px 0',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: '32px',
    fontSize: '1.25em',
    color: '#888',
    cursor: 'pointer',
    fontFamily: 'Segoe UI, Arial, sans-serif'
};

const activeColor = '#115740';

function haversineDistance([lat1, lon1]: [number, number], [lat2, lon2]: [number, number]) {
    function toRad(x: number) { return x * Math.PI / 180; }
    const R = 3958.8; // Earth radius in miles
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
}

const Sidebar: React.FC<{ 
    onLogout: () => void; 
    user: { name: string; job_role: string; user_id: number } | null; 
    onCreateUser?: () => void;
    isCollapsed: boolean;
    onToggle: () => void;
    windowWidth: number;
}> = ({ onLogout, user, onCreateUser, isCollapsed, onToggle, windowWidth }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isActive = (path: string) => location.pathname === path;

    // RBAC: Check if user has access to specific menu items
    const hasAccess = (menuItem: string) => {
        if (!user) return false;
        
        switch (user.job_role) {
            case 'Admin':
                return true;
            case 'Manager':
                return ['analytics', 'waste-tickets', 'projects', 'waste-sites'].includes(menuItem);
            case 'Driver':
                return false;
            default:
                return false;
        }
    };

    const isMobile = windowWidth <= 1400;

    return (
        <>
            {/* Mobile/Collapsed Overlay */}
            {isCollapsed && isMobile && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.5)',
                        zIndex: 998
                    }}
                    onClick={onToggle}
                />
            )}

            {/* Sidebar */}
            <div style={{
                width: '260px',
                background: '#fff',
                color: '#222',
                height: '100vh',
                padding: '32px 0 16px 0',
                display: 'flex',
                flexDirection: 'column',
                position: 'fixed',
                boxShadow: '2px 0 8px rgba(0,0,0,0.05)',
                fontFamily: 'Segoe UI, Arial, sans-serif',
                boxSizing: 'border-box',
                left: isMobile && !isCollapsed ? '-260px' : '0',
                transition: 'left 0.3s ease',
                zIndex: 999
            }}>
                {/* REMOVED: Close button - using main hamburger button instead */}

                <div style={{ width: '100%' }}>
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', marginBottom: '16px' }}>
                        <img 
                            src="/images/Galldris-Avatar.jpg" 
                            alt="Galldris Logo" 
                            style={{ width: '170px', height: '140px', objectFit: 'contain', margin: '0 16px' }} 
                        />
                        <div style={{ width: '100%', textAlign: 'center', fontSize: '1.1em', fontWeight: 500, color: '#888', marginBottom: '0' }}>
                            Waste Ticket System
                        </div>
                    </div>
                    <ul style={{ listStyle: 'none', padding: 0, width: '100%', margin: 0 }}>
                        {/* Your existing menu items stay the same... */}
                        
                        {/* Analytics */}
                        {hasAccess('analytics') && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/' ? '#115740' : '#888',
                                    fontWeight: location.pathname === '/' ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaTachometerAlt style={{ marginRight: '20px', color: location.pathname === '/' ? '#115740' : undefined }} />
                                <span>Analytics</span>
                            </li>
                        )}

                        {/* Rest of your existing menu items... */}
                        {hasAccess('waste-tickets') && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/waste-tickets' ? '#115740' : '#888',
                                    fontWeight: location.pathname === '/waste-tickets' ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/waste-tickets');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaTrash style={{ marginRight: '20px', color: location.pathname === '/waste-tickets' ? '#115740' : undefined }} />
                                <span>Waste Tickets</span>
                            </li>
                        )}

                        {/* Projects - Available to Admin and Manager */}
                        {hasAccess('projects') && (
                            <li
                                style={{
                                    ...navItemStyle,
                                    color: isActive('/projects') ? activeColor : navItemStyle.color,
                                    fontWeight: isActive('/projects') ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/projects');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaProjectDiagram style={{ marginRight: '20px', color: isActive('/projects') ? activeColor : undefined }} />
                                <span>Projects</span>
                            </li>
                        )}

                        {/* Waste Sites - Available to Admin and Manager */}
                        {hasAccess('waste-sites') && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/waste-dropoff-sites' ? '#115740' : '#888',
                                    fontWeight: location.pathname === '/waste-dropoff-sites' ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/waste-dropoff-sites');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaTrash style={{ marginRight: '20px', color: location.pathname === '/waste-dropoff-sites' ? '#115740' : undefined }} />
                                <span>Waste Sites</span>
                            </li>
                        )}

                        {/* Waste Carriers - Admin Only */}
                        {user?.job_role === 'Admin' && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/waste-carriers' ? '#115740' : '#888',
                                    fontWeight: location.pathname === '/waste-carriers' ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/waste-carriers');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaTruck style={{ marginRight: '20px', color: location.pathname === '/waste-carriers' ? '#115740' : undefined }} />
                                <span>Waste Carriers</span>
                            </li>
                        )}

                        {/* Users - Admin Only */}
                        {user?.job_role === 'Admin' && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/users' ? '#115740' : '#888',
                                    fontWeight: location.pathname === '/users' ? 600 : undefined,
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    navigate('/users');
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaUsers style={{ marginRight: '20px', color: location.pathname === '/users' ? '#115740' : undefined }} />
                                <span>Users</span>
                            </li>
                        )}

                        {/* Create User - Admin Only */}
                        {user?.job_role === 'Admin' && (
                            <li
                                style={{
                                    margin: '32px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    paddingLeft: '32px',
                                    fontSize: '1.25em',
                                    color: location.pathname === '/create-user' ? '#115740' : '#1976d2',
                                    fontWeight: location.pathname === '/create-user' ? 600 : 600,
                                    marginBottom: '16px',
                                    cursor: 'pointer'
                                }}
                                onClick={() => {
                                    if (onCreateUser) onCreateUser();
                                    if (isMobile) onToggle();
                                }}
                            >
                                <FaUserPlus style={{ marginRight: '20px', color: location.pathname === '/create-user' ? '#115740' : '#1976d2' }} />
                                <span>Create User</span>
                            </li>
                        )}
                    </ul>
                </div>
                {user && (
                    <div style={{ width: '100%', marginTop: 'auto', marginBottom: '24px' }}>
                        <div
                            style={{
                                ...logoutItemStyle,
                                width: 'calc(100% - 32px)',
                                margin: '0 auto 12px auto',
                                borderRadius: '8px',
                                background: '#fff',
                                justifyContent: 'flex-start',
                                cursor: 'pointer',
                                height: '44px',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                            onClick={onLogout}
                        >
                            <FaSignOutAlt style={{ marginRight: '20px', color: '#d32f2f' }} />
                            <span>Logout</span>
                        </div>
                        <div style={{
                            width: '100%',
                            margin: 0,
                            background: '#f4f4f4',
                            borderRadius: '8px',
                            padding: '16px 24px',
                            fontSize: '1.1em',
                            color: '#222',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            boxSizing: 'border-box',
                            justifyContent: 'flex-start'
                        }}>
                            <FaUserCircle style={{ marginRight: '16px', fontSize: '2em', color: '#1976d2' }} />
                            <div>
                                <div>{user.name}</div>
                                <div style={{ fontSize: '0.95em', color: '#555' }}>{user.job_role}</div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
};

const Dashboard: React.FC<{ user: { name: string; job_role: string; user_id: number; site_ids?: string[] } }> = ({ user }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [journeys, setJourneys] = useState<any[]>([]);
    const [selectedJourney, setSelectedJourney] = useState<string | null>(null);
    const [allSites, setAllSites] = useState<{ site_id: string; site_name: string }[]>([]);
    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState<string | null>(null);

    const totalTonnage = journeys
        .map(j => Number(j.weight) || 0)
        .reduce((a, b) => a + b, 0);

    const fetchTickets = () => {
        if (selectedSites.length > 0) {
            axios.get('/api/waste-tickets', { withCredentials: true, baseURL: API_CONFIG.BASE_URL, params: { site_ids: selectedSites.join(',') } })
                .then(res => setJourneys(res.data));
        } else {
            setJourneys([]);
        }
    };

    // --- Tonnage per Month ---
    const tonnagePerMonth: { month: string; tonnage: number }[] = [];
    const monthMap: { [month: string]: number } = {};

    journeys.forEach(j => {
        let date = null;
        if (j.date) {
            date = dayjs(j.date, 'YYYY-MM-DD HH:mm:ss');
            if (!date.isValid()) {
                date = dayjs(j.date, 'YYYY-MM-DD');
            }
            if (!date.isValid()) {
                date = dayjs(j.date, 'DD/MM/YYYY');
            }
        }
        if (date && date.isValid()) {
            const month = date.format('YYYY-MM');
            const weight = Number(j.weight) || 0;
            monthMap[month] = (monthMap[month] || 0) + weight;
        }
    });
    Object.entries(monthMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .forEach(([month, tonnage]) => {
            tonnagePerMonth.push({ month, tonnage });
        });

    // --- Tonnage per Material ---
    const materialMonthMap: { [material: string]: { [month: string]: number } } = {};

    journeys.forEach(j => {
        let date = null;
        if (j.date) {
            date = dayjs(j.date, 'YYYY-MM-DD HH:mm:ss');
            if (!date.isValid()) date = dayjs(j.date, 'YYYY-MM-DD');
            if (!date.isValid()) date = dayjs(j.date, 'DD/MM/YYYY');
        }
        if (date && date.isValid()) {
            const month = date.format('YYYY-MM');
            const material = j.waste_description || 'Unknown';
            const weight = Number(j.weight) || 0;
            if (!materialMonthMap[material]) materialMonthMap[material] = {};
            materialMonthMap[material][month] = (materialMonthMap[material][month] || 0) + weight;
        }
    });
    const materialMap: { [material: string]: number } = {};
    journeys.forEach(j => {
        const material = j.waste_description || 'Unknown';
        const weight = Number(j.weight) || 0;
        materialMap[material] = (materialMap[material] || 0) + weight;
    });
    const tonnagePerMaterial = Object.entries(materialMap)
        .map(([material, tonnage]) => ({ material, tonnage }))
        .sort((a, b) => b.tonnage - a.tonnage);

    useEffect(() => {
        axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL }).then(res => {
            setAllSites(res.data);
            // Default: all sites for Admin, manager's sites for Manager
            if (user.job_role === 'Admin') {
                setSelectedSites(res.data.map((s: any) => s.site_id));
            } else if (user.job_role === 'Manager' && user.site_ids) {
                setSelectedSites(user.site_ids);
            }
        });
    }, [user]);

    // WasteTicketDialog definition
    const [dropoffSites, setDropoffSites] = useState<{ id: number; name: string }[]>([]);
    const WasteTicketDialog: React.FC<{ open: boolean; onClose: () => void; userId: number }> = ({ open, onClose, userId }) => {
        const [description, setDescription] = useState('');
        const [collectionDate, setCollectionDate] = useState<Date | null>(null);
        const [collectionTime, setCollectionTime] = useState<Date | null>(null);
        const [siteId, setSiteId] = useState('');
        const [destination, setDestination] = useState('');
        const [sites, setSites] = useState<{ site_id: string; site_name: string }[]>([]);
        const [dropoffSites, setDropoffSites] = useState<{ id: number; name: string }[]>([]);
        const [error, setError] = useState('');
        const [loading, setLoading] = useState(false);
        const [vehicleId, setVehicleId] = useState('');
        const [driverId, setDriverId] = useState('');
        const [drivers, setDrivers] = useState<{ user_id: number; name: string }[]>([]);

        const formatTime = (date: Date | null) => {
            if (!date) return '';
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        };

        useEffect(() => {
            if (open) {
                axios.get('/api/waste-dropoff-sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
                    .then(res => {
                        const activeSites = res.data.filter((site: any) => site.is_active);
                        setDropoffSites(activeSites);
                    });
                axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL }).then(res => setSites(res.data));
                axios.get('/api/users?role=Driver', { withCredentials: true, baseURL: API_CONFIG.BASE_URL }).then(res => setDrivers(res.data));
            }
        }, [open]);

        useEffect(() => {
            async function fetchDistances() {
            }
            if (journeys.length > 0) fetchDistances();
        }, [journeys]);

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setError('');
            setLoading(true);

            if (
                !description.trim() ||
                !collectionDate ||
                !collectionTime ||
                !siteId.trim() ||
                !destination.trim() ||
                !userId ||
                !vehicleId.trim() ||
                !driverId
            ) {
                setError('All fields are required');
                setLoading(false);
                return;
            }

            try {
                const timeStr = collectionTime
                    ? collectionTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })
                    : '';
                const dateStr = collectionDate
                    ? collectionDate.toISOString().split('T')[0]
                    : '';
                const collection_datetime = new Date(
                    `${dateStr}T${collectionTime?.toTimeString().slice(0,5)}`
                ).toISOString();

                await axios.post('/api/waste-tickets', {
                    description,
                    collection_datetime,
                    site_id: siteId,
                    destination,
                    created_by: userId,
                    vehicle: vehicleId,
                    driver_id: driverId
                });
                setLoading(false);
                onClose();
                fetchTickets(); // <-- Refresh tickets after submission
            } catch (err: any) {
                setLoading(false);
                setError(err.response?.data?.error || 'Failed to create ticket');
            }
        };

        if (!open) return null;

        return (
            <>
                {pickerGlobalStyles}
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        background: 'rgba(0,0,0,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}
                    onClick={e => {
                        if (e.target === e.currentTarget) onClose();
                    }}
                >
                    <form
                        onSubmit={handleSubmit}
                        onClick={e => e.stopPropagation()}
                        style={{
                            background: '#fff',
                            padding: '32px 36px',
                            borderRadius: '18px',
                            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
                            minWidth: '370px',
                            maxWidth: '95vw',
                            textAlign: 'left',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '18px',
                            position: 'relative'
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 600, fontSize: '1.25em', color: '#115740', marginRight: 8 }}>Generate Waste Ticket</span>
                            <button
                                type="button"
                                onClick={onClose}
                                style={{
                                    marginLeft: 'auto',
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5em',
                                    color: '#888',
                                    cursor: 'pointer',
                                    padding: 0
                                }}
                                aria-label="Close"
                            >
                                ×
                            </button>
                        </div>
                        <div>
                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>What is being collected *</label>
                            <input
                                type="text"
                                value={description}
                                onChange={e => setDescription(e.target.value)}
                                placeholder="Describe the waste"
                                style={customInputStyle}
                                required
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Vehicle Registration *</label>
                            <input
                                type="text"
                                value={vehicleId}
                                onChange={e => setVehicleId(e.target.value)}
                                placeholder="Vehicle Registration"
                                style={customInputStyle}
                                required
                            />
                        </div>
                        <div>
                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Driver *</label>
                            <select
                                value={driverId}
                                onChange={e => setDriverId(e.target.value)}
                                style={{
                                    ...customInputStyle,
                                    height: '52px',
                                    fontSize: '1.15em',
                                    padding: '14px 12px'
                                }}
                                required
                            >
                                <option value="" disabled>Select driver</option>
                                {drivers.map(driver => (
                                    <option key={driver.user_id} value={driver.user_id}>
                                        {driver.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <div>
                                <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Collection Date *</label>
                                <DatePicker
                                    value={collectionDate}
                                    onChange={setCollectionDate}
                                    slotProps={{
                                        textField: {
                                            required: true,
                                            variant: "standard",
                                            InputProps: {
                                                disableUnderline: true,
                                                style: {
                                                    ...customInputStyle,
                                                    minHeight: '40px',
                                                    height: '40px',
                                                    width: '100%',
                                                    padding: '12px',
                                                    boxSizing: 'border-box'
                                                }
                                            },
                                            inputProps: {
                                                style: {
                                                    minHeight: '40px',
                                                    height: '40px',
                                                    width: '100%',
                                                    padding: '0',
                                                    boxSizing: 'border-box'
                                                }
                                            }
                                        }
                                    }}
                                    sx={{
                                        width: '100%'
                                    }}
                                />
                            </div>
                        </LocalizationProvider>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <div>
                                <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Collection Time *</label>
                                <StaticTimePicker
                                    orientation="landscape"
                                    value={collectionTime}
                                    onChange={setCollectionTime}
                                    ampm={true}
                                    displayStaticWrapperAs="desktop"
                                    slots={{
                                        actionBar: () => null
                                    }}
                                />
                                <input
                                    type="text"
                                    value={formatTime(collectionTime)}
                                    readOnly
                                    style={customInputStyle}
                                    placeholder="Selected Time"
                                />
                            </div>
                        </LocalizationProvider>
                        <div>
                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Site *</label>
                            <div style={inputWrapperStyle}>
                                <select
                                    value={siteId}
                                    onChange={e => setSiteId(e.target.value)}
                                    style={{
                                        ...customInputStyle,
                                        height: '52px',
                                        fontSize: '1.15em',
                                        padding: '14px 12px',
                                        paddingRight: 40,
                                        appearance: 'none' as const
                                    }}
                                    required
                                >
                                    <option value="" disabled>Select site</option>
                                    {sites.map(site => (
                                        <option key={site.site_id} value={site.site_id}>
                                            {site.site_id} - {site.site_name}
                                        </option>
                                    ))}
                                </select>
                                <FaChevronDown style={iconStyle} />
                            </div>
                        </div>
                        <div>
                            <label style={{ fontWeight: 500, marginBottom: 4, display: 'block' }}>Destination *</label>
                            <select
                                value={destination}
                                onChange={e => setDestination(e.target.value)}
                                style={{
                                    ...customInputStyle,
                                    height: '52px',
                                    fontSize: '1.15em',
                                    padding: '14px 12px'
                                }}
                                required
                            >
                                <option value="" disabled>Select dropoff site</option>
                                {dropoffSites.map(site => (
                                    <option key={site.id} value={site.name}>
                                        {site.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                ...customInputStyle,
                                background: '#115740',
                                color: '#fff',
                                border: 'none',
                                fontWeight: 600,
                                fontSize: '1.08em',
                                marginTop: '8px',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                boxShadow: '0 2px 8px rgba(17,87,64,0.08)'
                            }}
                        >
                            {loading ? 'Generating...' : 'Generate Waste Ticket'}
                        </button>
                        {error && <div style={{ color: '#d32f2f', marginTop: 6, fontWeight: 500 }}>{error}</div>}
                    </form>
                </div>
            </>
        );
    };

    useEffect(() => {
        fetchTickets();
    }, [selectedSites]);

    const renderSiteFilter = () => {
        // Dropdown style
        const dropdownStyle: React.CSSProperties = {
            position: 'relative',
            display: 'inline-block',
            width: 320,
            marginBottom: 24,
            textAlign: 'left'
        };
        const dropdownBtnStyle: React.CSSProperties = {
            width: '100%',
            padding: '8px 16px',
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: '1rem',
            background: '#fff',
            cursor: 'pointer',
            textAlign: 'left',
            fontWeight: 500
        };
        const dropdownMenuStyle: React.CSSProperties = {
            position: 'absolute',
            top: '110%',
            left: 0,
            width: '100%',
            background: '#fff',
            border: '1px solid #ccc',
            borderRadius: 6,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 100,
            padding: 8,
            maxHeight: 260,
            overflowY: 'auto'
        };

        if (user.job_role === 'Admin') {
            const selectedLabels = allSites
                .filter(site => selectedSites.includes(site.site_id))
                .map(site => `${site.site_id} - ${site.site_name}`);
            return (
                <div style={dropdownStyle}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Filter by Site:</div>
                    <button
                        type="button"
                        style={dropdownBtnStyle}
                        onClick={() => setSiteDropdownOpen(open => !open)}
                    >
                        {selectedLabels.length === 0
                            ? 'All Sites'
                            : selectedLabels.length === 1
                                ? selectedLabels[0]
                                : `${selectedLabels.length} sites selected`}
                        <span style={{ float: 'right', fontSize: '1.2em', color: '#888' }}>▼</span>
                    </button>
                    {siteDropdownOpen && (
                        <div style={dropdownMenuStyle}>
                            {allSites.map(site => (
                                <div key={site.site_id} style={{ marginBottom: 6 }}>
                                    <label
                                        style={{
                                            display: 'block',
                                            cursor: 'pointer',
                                            whiteSpace: 'normal',
                                            wordBreak: 'break-word',
                                            lineHeight: 1.4,
                                            width: '100%',
                                            marginBottom: 6
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedSites.includes(site.site_id)}
                                            onChange={e => {
                                                if (e.target.checked) {
                                                    setSelectedSites([...selectedSites, site.site_id]);
                                                } else {
                                                    setSelectedSites(selectedSites.filter(id => id !== site.site_id));
                                                }
                                            }}
                                            style={{ marginRight: 8, verticalAlign: 'top' }}
                                        />
                                        <span style={{ display: 'inline-block', minWidth: 90, maxWidth: 110, fontWeight: 700 }}>
                                            {site.site_id}
                                        </span>
                                        <span style={{ display: 'inline', wordBreak: 'break-word' }}>
                                            {' - '}{site.site_name}
                                        </span>
                                    </label>
                                </div>
                            ))}
                            <button
                                type="button"
                                style={{
                                    marginTop: 8,
                                    padding: '6px 16px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#eee',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSiteDropdownOpen(false)}
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            );
        } else if (user.job_role === 'Manager') {
            const managerSites = allSites.filter(site => user.site_ids?.includes(site.site_id));
            const selectedLabels = managerSites
                .filter(site => selectedSites.includes(site.site_id))
                .map(site => `${site.site_id} - ${site.site_name}`);
            
            return (
                <div style={dropdownStyle}>
                    <div style={{ fontWeight: 600, marginBottom: 8 }}>Your Assigned Sites:</div>
                    <button
                        type="button"
                        style={dropdownBtnStyle}
                        onClick={() => setSiteDropdownOpen(open => !open)}
                    >
                        {selectedLabels.length === 0
                            ? 'No sites selected'
                            : selectedLabels.length === 1
                                ? selectedLabels[0]
                                : `${selectedLabels.length} sites selected`}
                        <span style={{ float: 'right', fontSize: '1.2em', color: '#888' }}>▼</span>
                    </button>
                    {siteDropdownOpen && (
                        <div style={dropdownMenuStyle}>
                            {managerSites.length === 0 ? (
                                <div style={{ fontStyle: 'italic', color: '#888', padding: 8 }}>
                                    No sites assigned to you
                                </div>
                            ) : (
                                managerSites.map(site => (
                                    <div key={site.site_id} style={{ marginBottom: 6 }}>
                                        <label
                                            style={{
                                                display: 'block',
                                                cursor: 'pointer',
                                                whiteSpace: 'normal',
                                                wordBreak: 'break-word',
                                                lineHeight: 1.4,
                                                width: '100%',
                                                marginBottom: 6
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedSites.includes(site.site_id)}
                                                onChange={e => {
                                                    if (e.target.checked) {
                                                        setSelectedSites([...selectedSites, site.site_id]);
                                                    } else {
                                                        setSelectedSites(selectedSites.filter(id => id !== site.site_id));
                                                    }
                                                }}
                                                style={{ marginRight: 8, verticalAlign: 'top' }}
                                            />
                                            <span style={{ display: 'inline-block', minWidth: 90, maxWidth: 110, fontWeight: 700 }}>
                                                {site.site_id}
                                            </span>
                                            <span style={{ display: 'inline', wordBreak: 'break-word' }}>
                                                {' - '}{site.site_name}
                                            </span>
                                        </label>
                                    </div>
                                ))
                            )}
                            <button
                                type="button"
                                style={{
                                    marginTop: 8,
                                    padding: '6px 16px',
                                    borderRadius: 6,
                                    border: 'none',
                                    background: '#eee',
                                    fontWeight: 500,
                                    cursor: 'pointer'
                                }}
                                onClick={() => setSiteDropdownOpen(false)}
                            >
                                Close
                            </button>
                        </div>
                    )}
                </div>
            );
        }
        return null;
    };

    const CARD_WIDTH = 340;
    const CARD_HEIGHT = 180;
    const CARD_TITLE_FONT_SIZE = '1.5em';
    const CARD_VALUE_FONT_SIZE = '2.8em';

    // Replace the Dashboard component's return section (around line 600) with:

    return (
        <div style={{ position: 'relative' }}>
            {/* Keep your existing CSS styles */}
       
            <h1 style={{ fontWeight: 600, marginBottom: 20 }}>Welcome to Waste Ticket Management</h1>

            {/* MOVED: Site filter below title instead of fixed position */}
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                marginBottom: 40,
                width: '100%'
            }}>
                {renderSiteFilter()}
            </div>
            
            {/* Rest of your existing content stays exactly the same */}
            <div className="dashboard-container">
                <div className="left-column">
                    <div className="map-container">
                        <WasteTicketMap
                            journeys={journeys}
                            onSelectJourney={id => setSelectedJourney(id)}
                        />
                    </div>
                    
                    <div className="stats-row">
                        <div className="stat-card">
                            <div style={{ fontSize: '1.2em', fontWeight: 600, color: '#115740', marginBottom: '12px' }}>
                                Total Waste Tickets
                            </div>
                            <div style={{ fontSize: '2.2em', fontWeight: 700, color: '#222' }}>
                                {journeys.length}
                            </div>
                        </div>
                        
                        <div className="stat-card">
                            <div style={{ fontSize: '1.2em', fontWeight: 600, color: '#115740', marginBottom: '12px' }}>
                                Last 30 Days
                            </div>
                            <div style={{ fontSize: '2.2em', fontWeight: 700, color: '#222' }}>
                                {
                                    journeys.filter(j => {
                                        if (!j.date) return false;
                                        let ticketDate = dayjs(j.date, 'YYYY-MM-DD HH:mm:ss');
                                        if (!ticketDate.isValid()) ticketDate = dayjs(j.date, 'YYYY-MM-DD');
                                        if (!ticketDate.isValid()) ticketDate = dayjs(j.date, 'DD/MM/YYYY');
                                        if (!ticketDate.isValid()) return false;
                                        const now = dayjs();
                                        const daysAgo = now.subtract(30, 'day');
                                        return ticketDate.isAfter(daysAgo) && ticketDate.isBefore(now.add(1, 'day'));
                                    }).length
                                }
                            </div>
                        </div>
                    </div>
                    
                    <div className="stats-row">
                        <div className="stat-card">
                            <div style={{ fontSize: '1.2em', fontWeight: 600, color: '#115740', marginBottom: '12px' }}>
                                Avg. Distance (mi)
                            </div>
                            <div style={{ fontSize: '2.2em', fontWeight: 700, color: '#222' }}>
                                {journeys.filter(j => typeof j.distance_mi === 'number').length > 0
                                    ? (
                                        journeys
                                            .filter(j => typeof j.distance_mi === 'number')
                                            .reduce((a, b) => a + b.distance_mi, 0) /
                                        journeys.filter(j => typeof j.distance_mi === 'number').length
                                    ).toFixed(1)
                                    : '—'}
                            </div>
                        </div>
                        
                        <div className="stat-card">
                            <div style={{ fontSize: '1.2em', fontWeight: 600, color: '#115740', marginBottom: '12px' }}>
                                Total Tonnage
                            </div>
                            <div style={{ fontSize: '2.2em', fontWeight: 700, color: '#222' }}>
                                {totalTonnage.toLocaleString(undefined, { maximumFractionDigits: 1 })}t
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="right-column">
                    <div className="chart-container material-chart">
                        <div style={{ fontWeight: 600, fontSize: '1.2em', marginBottom: 16, color: '#115740' }}>
                            Tonnage by Material Type
                        </div>
                         <ResponsiveContainer width="100%" height={380}>
                            <BarChart 
                                data={tonnagePerMaterial} 
                                layout="vertical" 
                                margin={{ top: 20, right: 40, left: 20, bottom: 20 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                
                                {/* X-axis shows tonnage values */}
                                <XAxis 
                                    type="number"
                                    domain={[0, 'dataMax + 1']}
                                    tickFormatter={(value) => `${value}t`}
                                />
                                
                                {/* Y-axis shows materials */}
                                <YAxis 
                                    type="category" 
                                    dataKey="material"
                                    width={120}
                                    tick={{ fontSize: 11, textAnchor: 'end' }}
                                    interval={0}
                                    axisLine={true}
                                    tickLine={true}
                                />
                                
                                <Tooltip 
                                    formatter={(value: number) => [`${value} tonnes`, 'Tonnage']}
                                    labelStyle={{ color: '#115740', fontWeight: 600 }}
                                />
                                
                                <Bar 
                                    dataKey="tonnage" 
                                    name="Tonnage (t)" 
                                    fill="#115740"
                                    barSize={30}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>

                    <div className="chart-container month-chart">
                        <div style={{ fontWeight: 600, fontSize: '1.2em', marginBottom: 16, color: '#115740' }}>
                            Total Tonnage per Month
                        </div>
                        <ResponsiveContainer width="100%" height={280}>
                            <LineChart 
                                data={tonnagePerMonth} 
                                margin={{ top: 20, right: 30, left: 10, bottom: 20 }}
                                onClick={e => {
                                    // e.activeLabel is the month string (e.g., "2025-11")
                                    if (e && e.activeLabel) {
                                        setSelectedMonth(e.activeLabel);
                                    }
                                }}
                            >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis 
                                    dataKey="month"
                                    padding={{ left: 20, right: 20 }}
                                />
                                <YAxis tickFormatter={(value) => `${value}t`} />
                                <Tooltip formatter={(value: number) => [`${value} tonnes`, 'Tonnage']} />
                                <Legend />
                                <Line
                                    type="monotone"
                                    dataKey="tonnage"
                                    name="Tonnage (t)"
                                    stroke="#115740"
                                    strokeWidth={2.5}
                                    dot={{ r: 4, strokeWidth: 2, fill: "#F8BB00" }}
                                    activeDot={{ r: 6 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                        {selectedMonth && (
                            <div
                                className="dashboard-modal-overlay"
                                style={{
                                    position: 'fixed',
                                    top: 0,
                                    left: 0,
                                    width: '100vw',
                                    height: '100vh',
                                    background: 'rgba(0,0,0,0.18)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 2000,
                                    padding: 0,
                                }}
                                onClick={() => setSelectedMonth(null)}
                            >
                                <div
                                    className="dashboard-modal-content"
                                    style={{
                                        background: '#fff',
                                        borderRadius: 12,
                                        padding: '6vw 4vw',
                                        maxWidth: 420,
                                        width: '92vw',
                                        minWidth: 0,
                                        boxShadow: '0 2px 24px rgba(0,0,0,0.18)',
                                        position: 'relative',
                                        maxHeight: '90vh',
                                        overflowY: 'auto',
                                        margin: '0 2vw',
                                    }}
                                    onClick={e => e.stopPropagation()}
                                >
                                    <h3 style={{
                                        color: '#115740',
                                        marginBottom: 12,
                                        fontSize: '1.2em',
                                        textAlign: 'center',
                                        wordBreak: 'break-word'
                                    }}>
                                        {selectedMonth} - Material Breakdown
                                    </h3>
                                    <table style={{
                                        width: '100%',
                                        borderCollapse: 'collapse',
                                        fontSize: '1em',
                                        wordBreak: 'break-word'
                                    }}>
                                        <thead>
                                            <tr>
                                                <th style={{ textAlign: 'left', padding: 6 }}>Material</th>
                                                <th style={{ textAlign: 'right', padding: 6 }}>Tonnage (t)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {Object.entries(
                                                Object.fromEntries(
                                                    Object.entries(materialMonthMap)
                                                        .map(([material, months]) => [material, months[selectedMonth] || 0])
                                                        .filter(([_, tonnage]) => Number(tonnage) > 0)
                                                )
                                            )
                                                .sort((a, b) => (b[1] as number) - (a[1] as number))
                                                .map(([material, tonnage]) => (
                                                    <tr key={material}>
                                                        <td style={{ padding: 6, textAlign: 'left' }}>{material}</td>
                                                        <td style={{ padding: 6, textAlign: 'right' }}>{(tonnage as number).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                    <button
                                        style={{
                                            marginTop: 16,
                                            background: '#115740',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: 6,
                                            padding: '8px 18px',
                                            fontWeight: 600,
                                            cursor: 'pointer',
                                            display: 'block',
                                            marginLeft: 'auto',
                                            marginRight: 'auto',
                                            fontSize: '1em'
                                        }}
                                        onClick={() => setSelectedMonth(null)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

const MainContent: React.FC = () => {
    const [user, setUser] = useState<{ name: string; job_role: string; user_id: number; site_ids?: string[] } | null>(null);
    const [checkingSession, setCheckingSession] = useState<boolean>(true);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 1920);
    const navigate = useNavigate();
    const location = useLocation();
    const showSidebar = location.pathname !== '/login';

    // Handle window resize - Update window width state
    useEffect(() => {
        const handleResize = () => {
            const newWidth = window.innerWidth;
            setWindowWidth(newWidth);
            
            if (newWidth > 1400) {
                setSidebarOpen(false); // Close mobile menu on desktop
            }
        };

        window.addEventListener('resize', handleResize);
        // Set initial width
        handleResize();
        
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        // If we're on the login page, clear user and stop session-checking
        if (location.pathname === '/login') {
            setUser(null);
            setCheckingSession(false);
        }
    }, [location.pathname]);

    const handleLogout = async () => {
        await axios.post('/api/logout', {}, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
        setUser(null);
        navigate('/login');
    };

    const handleCreateUser = () => {
        navigate('/create-user');
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };

    useEffect(() => {
        // Run initial session check once (unless already on /login)
        if (location.pathname === '/login') return;
        if (!checkingSession) return;

        axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(res => {
                setUser({
                    name: res.data.name,
                    job_role: res.data.job_role,
                    user_id: res.data.user_id,
                    site_ids: res.data.site_ids || []
                });
            })
            .catch(err => {
                setUser(null);
                navigate('/login');
            })
            .finally(() => setCheckingSession(false));
    }, [checkingSession, location.pathname]);

    useEffect(() => {
        setupAxiosInterceptors(setUser, navigate);
    }, [setUser, navigate]);

    // Prevent rendering routes (and immediate Navigate -> /login) until session check completes
    if (checkingSession) {
        return <div />; // replace with spinner if desired
    }

    const isAdmin = user?.job_role === 'Admin';
    const isDesktop = windowWidth > 1400; // Use state instead of direct window.innerWidth

    return (
        <div style={{ display: 'flex', fontFamily: 'Segoe UI, Arial, sans-serif', background: '#f4f4f4', minHeight: '100vh' }}>
            {/* Hamburger Menu Button - Fixed position, only icon changes */}
            {showSidebar && user && !isDesktop && (
                <button
                    onClick={toggleSidebar}
                    style={{
                        position: 'fixed',
                        top: 20,
                        left: 20, // Keep in same position always
                        zIndex: 1001, // Higher than sidebar z-index (999)
                        background: '#115740',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px',
                        fontSize: '1.2em',
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                        // Removed transition since button doesn't move
                    }}
                >
                    {sidebarOpen ? <FaTimes /> : <FaBars />} {/* Only icon changes */}
                </button>
            )}

            {/* Sidebar - Desktop always visible, Mobile based on sidebarOpen */}
            {showSidebar && user && (isDesktop || sidebarOpen) && (
                <Sidebar
                    onLogout={handleLogout}
                    user={user}
                    onCreateUser={isAdmin ? handleCreateUser : undefined}
                    isCollapsed={sidebarOpen}
                    onToggle={toggleSidebar}
                    windowWidth={windowWidth}
                />
            )}

            {/* Main Content */}
            <div style={{ 
                marginLeft: showSidebar && user && isDesktop ? '260px' : '0', 
                padding: showSidebar && user && !isDesktop ? '80px 40px 60px 40px' : '60px 40px', // More side margins
                width: '100%', 
                textAlign: 'center', 
                position: 'relative',
                transition: 'margin-left 0.3s ease',
                boxSizing: 'border-box'
            }}>
                <Routes>
                    <Route path="/login" element={<Login setUser={userObj => setUser(userObj as any)} />} />
                    <Route path="/create-user" element={user && isAdmin ? <CreateUser isAdmin={isAdmin} /> : <Navigate to="/login" />} />
                    <Route path="/" element={user ? <Dashboard user={user} /> : <Navigate to="/login" />} />
                    <Route path="/waste-tickets" element={user ? <WasteTickets user={user} /> : <Navigate to="/login" />} />
                    <Route path="/waste-carriers" element={user ? <WasteCarriers /> : <Navigate to="/login" />} />
                    <Route path="/projects" element={user ? <Projects user={user} /> : <Navigate to="/login" />} />
                    <Route path="/waste-dropoff-sites" element={user ? <WasteDropoffSites user={user} /> : <Navigate to="/login" />} />
                    <Route path="/users" element={user ? <Users /> : <Navigate to="/login" />} />
                </Routes>
            </div>
        </div>
    );
};

const App: React.FC = () => (
    <Router>
        <MainContent />
    </Router>
);

export default App;